import React, { forwardRef, useState } from 'react';
import { useRegistrationCoreTenders } from '~src/hooks/api-hooks/useRegistrationCoreTenders';
import {
  PrintTableItem,
  TendersPrintTable,
} from '~components/molecules/Tabs/TenderTabs/components/Tenders/TendersPrintTable';
import { loadTendersRegistrationCoreTenderFolders } from '~src/services/api';
import { ArchiveRegistrationCore, TenderSubFolderCoreVM } from '~src/types/db';
import { useSharedTenderFolders } from '~src/hooks/api-hooks/useSharedTenderFolders';
import {
  loadPublicTenderCoreFolder,
  loadTendersAttachmentsShare,
  loadTendersSubFoldersShare,
} from '~src/services/api/shares';
import { Button, Empty, Spin } from 'antd';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from '~components/molecules/DetailsLayouts/components/LeftDetailsBlock/LeftDetailsBlock.style';
import { useIntl } from 'react-intl';
import { usePublicTenderCoreFolders } from '~src/hooks/api-hooks/usePublicTenderCoreFolders';

type RegistrationAttachment = {
  id?: string | number;
  name: string;
  description?: string | null;
};

export type TenderDocumentPrintProps = {
  tenderId: string;
  isStartFetch?: boolean;
  isExpandable?: boolean;
  isPublicTender?: boolean;
  shareId?: string;
  shareKey?: string;
  isValidShare?: boolean;
  registrationAttachments?: RegistrationAttachment[];
};

const mapRegistrationAttachmentsToPrintItems = (
  attachments?: RegistrationAttachment[]
): PrintTableItem[] => {
  return (attachments || []).map((attachment, index) => ({
    ...attachment,
    id: attachment.id ?? `${attachment.name}-${index}`,
    description: attachment.description ?? undefined,
    isAttachment: true,
  })) as PrintTableItem[];
};

const TenderSharesDocumentExpandable = forwardRef<
  HTMLDivElement,
  TenderDocumentPrintProps
>(
  (
    {
      tenderId,
      isStartFetch,
      isExpandable,
      isValidShare,
      shareId,
      shareKey,
      registrationAttachments,
      isPublicTender,
    },
    ref
  ) => {
    const styles = useStyles(styleFn);
    const intl = useIntl();

    const [isLoading, setIsLoading] = useState(true);
    const [itemsList, setItemsList] = useState<PrintTableItem[]>([]);

    React.useEffect(() => {
      if (isValidShare === false) {
        setIsLoading(false);
        setItemsList([]);
      }
    }, [isValidShare]);

    React.useEffect(() => {
      if (isLoading || !registrationAttachments?.length) return;

      setItemsList((prev) => {
        const hasAttachments = prev.some((item) => item.isAttachment);

        if (hasAttachments) {
          return prev;
        }

        return [
          ...prev,
          ...mapRegistrationAttachmentsToPrintItems(registrationAttachments),
        ];
      });
    }, [registrationAttachments, isLoading]);

    const publicTenderFolders = usePublicTenderCoreFolders(tenderId, {
      enabled: Boolean(
        isStartFetch && isPublicTender && tenderId && isValidShare !== false
      ),
      retry: false,
      onSuccess: async (data) => {
        try {
          const folderList = await Promise.all(
            (data || []).map(async (folder) => {
              const restSubfoldersList = await loadPublicTenderCoreFolder({
                tenderShareId: tenderId,
                rootFolderId: String(folder.id),
              });

              const rootTenderFolder =
                restSubfoldersList.find(({ parent_id }) => !parent_id) ??
                folder;

              const treeFolder = createFolderStructure(restSubfoldersList, {
                ...rootTenderFolder,
                name: folder.name,
                children: [],
                isAttachment: false,
              });

              return treeFolder;
            })
          );

          setItemsList([
            ...folderList,
            ...mapRegistrationAttachmentsToPrintItems(registrationAttachments),
          ]);

          setIsLoading(false);
        } catch (error) {
          console.error('Public tender folders are forbidden:', error);

          setItemsList(
            mapRegistrationAttachmentsToPrintItems(registrationAttachments)
          );
          setIsLoading(false);
        }
      },
      onError: () => {
        setItemsList(
          mapRegistrationAttachmentsToPrintItems(registrationAttachments)
        );
        setIsLoading(false);
      },
    });

    const sharedTenderFolders = useSharedTenderFolders(
      {
        id: shareId,
        key: shareKey,
      },
      {
        enabled:
          !isPublicTender &&
          !!shareId &&
          !!shareKey &&
          !!tenderId &&
          isValidShare !== false,
        retry: false,
        onSuccess: async (data) => {
          try {
            const folderList = await Promise.all(
              data.map(async (folder) => {
                const restSubfoldersList = await loadTendersSubFoldersShare({
                  id: shareId as string,
                  key: shareKey as string,
                  rootFolderId: String(folder.id),
                });

                const rootTenderFolder = restSubfoldersList.filter(
                  ({ parent_id }) => !parent_id
                )[0];

                const treeFolder = createFolderStructure(restSubfoldersList, {
                  ...rootTenderFolder,
                  name: folder.name,
                  children: [],
                  isAttachment: false,
                });

                return treeFolder;
              })
            );

            let attachmentItems = mapRegistrationAttachmentsToPrintItems(
              registrationAttachments
            );

            if (attachmentItems.length === 0) {
              try {
                const attachments = await loadTendersAttachmentsShare({
                  id: shareId,
                  key: shareKey,
                });

                attachmentItems = attachments?.map((attachment) => ({
                  ...attachment,
                  isAttachment: true,
                }));
              } catch (error) {
                console.error(
                  'Tender share attachments are forbidden, using registration attachments fallback:',
                  error
                );
              }
            }

            setItemsList([...folderList, ...(attachmentItems || [])]);

            setIsLoading(false);
          } catch (error) {
            console.error('Tender is closed or files forbidden:', error);
            setIsLoading(false);
            setItemsList(
              mapRegistrationAttachmentsToPrintItems(registrationAttachments)
            );
          }
        },
        onError: () => {
          setIsLoading(false);
          setItemsList(
            mapRegistrationAttachmentsToPrintItems(registrationAttachments)
          );
        },
      }
    );

    // const handleExpandableChange = (
    //   expanded: boolean,
    //   record: PrintTableItem
    // ) => {
    //   if (!isExpandable) return;
    //
    //   if (expanded) {
    //     setExpandedRowKeys((prev) => [...prev, String(record.id)]);
    //   } else {
    //     setExpandedRowKeys((prev) =>
    //       prev.filter((key) => key !== String(record.id))
    //     );
    //   }
    // };

    const EmptyBlock: React.FC = (props) => {
      return (
        <Empty
          css={{
            padding: '5vh 0',
            margin: '0',
          }}
          description={props.children}></Empty>
      );
    };

    /* PRINT ONLY LAYOUT COMPONENT */
    if (isLoading) {
      return (
        <div
          style={{
            padding: '5vh 0',
            display: 'flex',
            justifyContent: 'center',
          }}>
          <Spin size="large" />
        </div>
      );
    }

    if (itemsList.length === 0) {
      return <EmptyBlock>{intl.formatMessage({ id: 'no-files' })}</EmptyBlock>;
    }

    return (
      <TendersPrintTable
        ref={ref}
        itemsList={itemsList}
        isListLoading={false}
      />
    );
  }
);

function createFolderStructure(
  folders: TenderSubFolderCoreVM,
  rootFolder: PrintTableItem
): PrintTableItem {
  const buildHierarchyFromRoot = (
    currentLevelRootFolder: PrintTableItem & {
      registrations?: ArchiveRegistrationCore[];
    }
  ) => {
    currentLevelRootFolder.children = [];

    const currentLevelChildren = folders.filter(
      ({ parent_id }) => parent_id === currentLevelRootFolder.id
    );

    for (const folder of currentLevelChildren) {
      const childFolder = buildHierarchyFromRoot({
        ...folder,
        isAttachment: false,
        children: [],
      });
      currentLevelRootFolder.children.push(childFolder);

      // childFolder.children?.push(
      //   ...folder.registrations.map(({ title, description, id }) => ({
      //     id,
      //     name: title,
      //     description,
      //     isAttachment: true,
      //   }))
      // );
    }

    const currentFolderRegistrations = currentLevelRootFolder.registrations?.map(
      ({ title, description, id }) => ({
        id,
        name: title,
        description,
        isAttachment: true,
      })
    );

    currentLevelRootFolder.children.push(...(currentFolderRegistrations || []));

    return currentLevelRootFolder;
  };

  return buildHierarchyFromRoot(rootFolder);
}

export default TenderSharesDocumentExpandable;
